import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, registerUser, checkSession } from "../services/authService";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null); // NUOVO: tracking scadenza sessione
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem("authToken");
    setUser(null);
    setPermissions({});
    setIsAuthenticated(false);
    setSessionExpiresAt(null); // NUOVO: reset scadenza sessione
    setInitialCheckDone(true);
    navigate("/login", { replace: true });
  }, [navigate]);

  // NUOVO: Funzione per aggiornare il token e la scadenza della sessione
  const updateSession = useCallback((newToken, expiresAt) => {
    if (newToken) {
      localStorage.setItem("authToken", newToken);
    }
    if (expiresAt) {
      setSessionExpiresAt(new Date(expiresAt));
    }
  }, []);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setInitialCheckDone(false);
    try {
      const data = await loginUser(username, password);
      if (data && data.token && data.user) {
        localStorage.setItem("authToken", data.token);
        setUser(data.user);
        setPermissions(data.permissions || {});
        setIsAuthenticated(true);
        
        // NUOVO: Imposta la scadenza della sessione se disponibile
        if (data.expiresAt) {
          setSessionExpiresAt(new Date(data.expiresAt));
        }
        
        navigate('/sisketron/macrofunzionipage', { replace: true });
        return data;
      } else {
        throw new Error("Invalid login response from server.");
      }

    } catch (error) {
      console.error("Login failed:", error);
      handleLogout();
      throw error;
    } finally {
      setLoading(false);
      setInitialCheckDone(true);
    }
  }, [navigate, handleLogout]);

  const register = useCallback(async (userData) => {
    setLoading(true);
    setInitialCheckDone(false);
    try {
        const data = await registerUser(userData);
        setLoading(false);
        setInitialCheckDone(true);
        return data;
    } catch(error) {
        console.error("Registration failed:", error);
        setLoading(false);
        setInitialCheckDone(true);
        throw error;
    }
  }, []);

  // Effetto per controllare la sessione all'avvio
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      setLoading(true);
      setInitialCheckDone(false);
      const token = localStorage.getItem("authToken");

      try {
        if (token) {
          const sessionData = await checkSession();
          if (sessionData && sessionData.user && isMounted) {
            setUser(sessionData.user);
            setPermissions(sessionData.permissions || {});
            setIsAuthenticated(true);
            
            // NUOVO: Imposta la scadenza della sessione
            if (sessionData.expiresAt) {
              setSessionExpiresAt(new Date(sessionData.expiresAt));
            }
          } else if (isMounted) {
            handleLogout();
          }
        } else if (isMounted) {
          setIsAuthenticated(false);
          setUser(null);
          setPermissions({});
          setSessionExpiresAt(null); // NUOVO: reset scadenza
        }
      } catch (error) {
        if (isMounted) {
          handleLogout();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setInitialCheckDone(true);
        }
      }
    };

    initializeAuth();

    const handleSessionExpiredEvent = () => {
        if (isMounted) {
            handleLogout();
        }
    };
    window.addEventListener("sessionExpired", handleSessionExpiredEvent);

    return () => {
      isMounted = false;
      window.removeEventListener("sessionExpired", handleSessionExpiredEvent);
    };
  }, [handleLogout]);

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      isAuthenticated,
      loading,
      initialCheckDone,
      sessionExpiresAt, // NUOVO: esposizione scadenza sessione
      updateSession, // NUOVO: funzione per aggiornare sessione
      login,
      logout: handleLogout,
      register
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}